import { InformationCircleIcon, XCircleIcon } from "@heroicons/react/20/solid";
import { FormEvent } from "react";

import { formatCountryDisplayName } from "../../lib/format-country-label";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Input } from "./input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./select";

type FormFieldKey =
	| "bankLocation"
	| "accountName"
	| "accountNumberOrIban"
	| "bicOrSwiftCode"
	| "clearingOrRoutingCode"
	| "payeeName"
	| "payeeNickname"
	| "addressLine1"
	| "addressLine2"
	| "city"
	| "country"
	| "state"
	| "postalCode";

export type AddBankAccountFormValues = Record<FormFieldKey, string>;

export type AddBankAccountOption = {
	value: string;
	label: string;
};

export type AddBankAccountFormErrors = Partial<
	Record<FormFieldKey | "bankId", string>
>;

export type AddBankAccountFormProps = {
	value: AddBankAccountFormValues;
	onChange: (value: AddBankAccountFormValues) => void;
	bankLocationOptions: AddBankAccountOption[];
	countryOptions: AddBankAccountOption[];
	errors?: AddBankAccountFormErrors;
	submissionError?: string;
	className?: string;
	isLoading?: boolean;
	onClose: () => void;
	onSubmit: (event: FormEvent<HTMLFormElement>) => void;
	isPayeeDetailsRequired?: boolean;
	submitButtonPlacement?: "inside" | "outside";
};

const getInputClassName = (hasError?: string) =>
	`${hasError ? "border-red-600 focus-visible:ring-red-600" : ""} shadow-[0px_1px_4px_rgba(0,0,0,0.12)] focus-visible:ring-4 focus-visible:ring-[#5CC3C0]/25 focus-visible:ring-offset-0 focus-visible:border-2 focus-visible:border-alta-dark-green`;

const getSelectClassName = (hasError?: string) =>
	`${hasError ? "border-red-600 focus-visible:ring-red-600" : ""} shadow-[0px_1px_4px_rgba(0,0,0,0.12)] data-[state=open]:border-2 data-[placeholder]:text-slate-500 data-[state=open]:border-alta-dark-green data-[state=open]:ring-4 data-[state=open]:ring-[#5CC3C0]/25 data-[state=open]:ring-offset-0`;

const Label = ({
	text,
	required = false,
}: {
	text: string;
	required?: boolean;
}) => (
	<label className="text-sm font-semibold text-brand-black">
		{text}
		{required ? <span className="text-red-600"> *</span> : null}
	</label>
);

export const AddBankAccountForm = ({
	value,
	onChange,
	bankLocationOptions,
	countryOptions,
	errors = {},
	submissionError,
	className,
	isLoading = false,
	onClose,
	onSubmit,
	isPayeeDetailsRequired = true,
	submitButtonPlacement = "inside",
}: AddBankAccountFormProps) => {
	const setField = (field: FormFieldKey, nextValue: string) => {
		onChange({ ...value, [field]: nextValue });
	};
	const selectedCountry = countryOptions.find(
		(option) => option.value === value.country,
	);
	const bankLocationTriggerLabel = value.bankLocation
		? formatCountryDisplayName(
				bankLocationOptions.find((o) => o.value === value.bankLocation)
					?.label ?? value.bankLocation,
			)
		: undefined;
	const countryTriggerLabel = value.country
		? formatCountryDisplayName(
				countryOptions.find((o) => o.value === value.country)?.label ??
					value.country,
			)
		: undefined;
	const normalizedCountryValue = value.country.trim().toLowerCase();
	const normalizedCountryLabel = selectedCountry?.label.trim().toLowerCase();
	const isSingaporeSelected =
		normalizedCountryValue === "singapore" ||
		normalizedCountryLabel === "singapore";
	const payeeErrors: AddBankAccountFormErrors = isPayeeDetailsRequired
		? errors
		: {};
	const addressLine1Error = payeeErrors.addressLine1;

	return (
		<form className="space-y-8" onSubmit={onSubmit}>
			<div
				className={cn(
					"w-full space-y-4 bg-[#F9FAFB] rounded-md p-4",
					className,
				)}
			>
				<div className="space-y-4">
					<div className="flex items-center justify-between">
						<h2 className="text-base font-semibold leading-6 font-600 text-[#4A5565]">
							Add new bank account
						</h2>
						<button
							type="button"
							onClick={onClose}
							className="h-5 w-5 opacity-60 hover:opacity-80"
							aria-label="Close"
						>
							<XCircleIcon className="h-full w-full" />
						</button>
					</div>
					<div className="flex flex-row rounded-md border border-blue-600 bg-blue-50">
						<div className="bg-blue-200 flex items-center justify-center w-16 rounded-l-md px-2">
							<InformationCircleIcon className="h-4 w-4 text-blue-700" />
						</div>
						<p className="p-2 text-xs font-light text-blue-700">
							The funds will be sent to the receiving bank account. If the
							account does not support the sent currency, the bank may convert
							the funds and apply fees.
						</p>
					</div>
				</div>

				<div className="space-y-2">
					<div className="space-y-1.5">
						<Label text="Bank location" required />
						<Select
							value={value.bankLocation}
							onValueChange={(nextValue) => setField("bankLocation", nextValue)}
						>
							<SelectTrigger
								className={`${getSelectClassName(errors.bankLocation)} w-1/2 border`}
							>
								<SelectValue placeholder="Select">
									{bankLocationTriggerLabel}
								</SelectValue>
							</SelectTrigger>
							<SelectContent className="max-h-[min(24rem,var(--radix-select-content-available-height))] overflow-y-auto radix-select-scrollbar-visible">
								{bankLocationOptions.map((option) => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						{errors.bankLocation ? (
							<p className="text-xs text-red-600">{errors.bankLocation}</p>
						) : null}
					</div>

					<div className="space-y-1.5">
						<Label text="Account name" required />
						<Input
							value={value.accountName}
							onChange={(event) => setField("accountName", event.target.value)}
							placeholder="For your personal reference (e.g. XYZ Multi-Currency Account)"
							className={getInputClassName(errors.accountName)}
						/>
						{errors.accountName ? (
							<p className="text-xs text-red-600">{errors.accountName}</p>
						) : null}
					</div>

					<div className="space-y-1.5">
						<Label text="Account number / IBAN" required />
						<Input
							value={value.accountNumberOrIban}
							onChange={(event) =>
								setField("accountNumberOrIban", event.target.value)
							}
							placeholder="Account number"
							className={getInputClassName(errors.accountNumberOrIban)}
						/>
						{errors.accountNumberOrIban ? (
							<p className="text-xs text-red-600">
								{errors.accountNumberOrIban}
							</p>
						) : null}
					</div>

					<div className="space-y-1.5">
						<Label text="BIC or SWIFT code" required />
						<Input
							value={value.bicOrSwiftCode}
							onChange={(event) =>
								setField("bicOrSwiftCode", event.target.value)
							}
							placeholder="Bank ID / SWIFT code"
							className={getInputClassName(errors.bicOrSwiftCode)}
						/>
						{errors.bicOrSwiftCode ? (
							<p className="text-xs text-red-600">{errors.bicOrSwiftCode}</p>
						) : null}
					</div>

					<div className="space-y-1.5">
						<Label text="Clearing or routing code" />
						<Input
							value={value.clearingOrRoutingCode}
							onChange={(event) =>
								setField("clearingOrRoutingCode", event.target.value)
							}
							placeholder="Clearing code (optional)"
							className={getInputClassName(errors.clearingOrRoutingCode)}
						/>
						{errors.clearingOrRoutingCode ? (
							<p className="text-xs text-red-600">
								{errors.clearingOrRoutingCode}
							</p>
						) : null}
					</div>
				</div>

				<div className="space-y-4 pt-6">
					<h3 className="text-lg font-extrabold">Payee details</h3>

					<div className="space-y-1.5">
						<Label text="Payee name" required={isPayeeDetailsRequired} />
						<Input
							value={value.payeeName}
							onChange={(event) => setField("payeeName", event.target.value)}
							placeholder="Payee name"
							className={getInputClassName(payeeErrors.payeeName)}
						/>
						{payeeErrors.payeeName ? (
							<p className="text-xs text-red-600">{payeeErrors.payeeName}</p>
						) : null}
					</div>

					<div className="space-y-1.5">
						<Label text="Payee nickname" required={isPayeeDetailsRequired} />
						<Input
							value={value.payeeNickname}
							onChange={(event) =>
								setField("payeeNickname", event.target.value)
							}
							placeholder="Payee nickname"
							className={getInputClassName(payeeErrors.payeeNickname)}
						/>
						{payeeErrors.payeeNickname ? (
							<p className="text-xs text-red-600">
								{payeeErrors.payeeNickname}
							</p>
						) : null}
					</div>

					<div className="space-y-1.5">
						<Label
							text="Residential address"
							required={isPayeeDetailsRequired}
						/>
						<div className="space-y-2">
							<Input
								value={value.addressLine1}
								onChange={(event) =>
									setField("addressLine1", event.target.value)
								}
								placeholder="Street address"
								className={getInputClassName(payeeErrors.addressLine1)}
							/>
							{addressLine1Error ? (
								<p className="text-xs text-red-600">{addressLine1Error}</p>
							) : null}
							<Input
								value={value.addressLine2}
								onChange={(event) =>
									setField("addressLine2", event.target.value)
								}
								placeholder="Apartment, suite, unit etc. (optional)"
								className={getInputClassName(payeeErrors.addressLine2)}
							/>
							<Input
								value={value.city}
								onChange={(event) => setField("city", event.target.value)}
								placeholder="City"
								className={getInputClassName(payeeErrors.city)}
							/>
						</div>
					</div>

					<div className="grid gap-2 grid-cols-3">
						<div className="space-y-1.5">
							<Select
								value={value.country}
								onValueChange={(nextValue) => setField("country", nextValue)}
							>
								<SelectTrigger
									className={`${getSelectClassName(errors.country)} border`}
								>
									<SelectValue placeholder="Country">
										{countryTriggerLabel}
									</SelectValue>
								</SelectTrigger>
								<SelectContent className="max-h-[min(24rem,var(--radix-select-content-available-height))] overflow-y-auto radix-select-scrollbar-visible">
									{countryOptions.map((option) => (
										<SelectItem key={option.value} value={option.value}>
											{option.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className="space-y-1.5">
							<Input
								value={value.state}
								onChange={(event) => setField("state", event.target.value)}
								placeholder="State"
								maxLength={100}
								className={getInputClassName(
									isSingaporeSelected ? undefined : payeeErrors.state,
								)}
								disabled={isSingaporeSelected}
							/>
						</div>

						<div className="space-y-1.5">
							<Input
								value={value.postalCode}
								onChange={(event) => setField("postalCode", event.target.value)}
								placeholder="Postal code"
								maxLength={20}
								className={getInputClassName(payeeErrors.postalCode)}
							/>
						</div>
					</div>
				</div>
				{submissionError ? (
					<div className="flex flex-row rounded-[4px] border border-red-600 bg-red-100 text-red-700">
						<div className="bg-red-300 flex items-center justify-center px-2 rounded-l-[4px]">
							<XCircleIcon className="h-4 w-4 text-red-700" />
						</div>
						<p className="p-2 text-xs leading-4 font-light text-red-700">
							{submissionError}
						</p>
					</div>
				) : null}
				{submitButtonPlacement === "inside" && (
					<Button
						type="submit"
						disabled={isLoading}
						className={
							isLoading
								? "bg-gray-400 text-white w-full"
								: "w-full hover:bg-alta-light-green/90"
						}
					>
						{isLoading ? "Loading..." : "Save bank details"}
					</Button>
				)}
			</div>

			{submitButtonPlacement === "outside" && (
				<Button
					type="submit"
					disabled={isLoading}
					className={
						isLoading
							? "bg-gray-400 text-white w-full"
							: "w-full hover:bg-alta-light-green/90"
					}
				>
					{isLoading ? "Loading..." : "Add new bank account"}
				</Button>
			)}
		</form>
	);
};
